//  ==========================================================================
//
//  Search
//
//  ==========================================================================
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import queryString from 'query-string';

import {
	updateUrl,
	getUrlFilters,
	doSearchParametersDiffer,
} from './utils';

import {
	getSearchInputValue,
	getActiveTab,
	getHasNextPage,
	getIncrementPageRequest,
	getLastSuccessfulSearch,
	getPage,
	getFilters,
	getSelectedFilters,
	isSearchEmpty,
	hasSelectedFilters,
	getDescription,
	getResults,
	getFeaturedResults,
	getFeaturedTitle,
	getResultsPerPage,
	getShouldSearch,
	getLoading,
	getError,
	getTotalMatches,
} from './store/selectors';
import {
	clearResults,
	setActiveTab,
	setSelectedFilters,
	incrementPage,
	removeFilter,
	resetSearch,
	setSearchInputValue,
	setShouldSearch,
	fetchResultsLoading,
	fetchResultsError,
	fetchResultsSuccess,
	toggleFilter,
} from './store/actions';

import {
	GridStandard,
	LoadMoreButton,
	MetaTags,
	SearchResultsFor,
	Tabbed,
} from '../';
import {
	StyledResults,
	StyledResultsInfo,
	StyledResultsInfoBlock,
	StyledResultsP,
	StyledNoResultsP,
	SearchForm,
	StyledLoadMoreContainer,
} from './styles';
import { runRequestREST } from './runRequest';
import { buildStateREST } from './buildState';

const Search = ( { location, getFilterText } ) => {

	const dispatch = useDispatch();

	const {
		activeTab,
		description,
		filters,
		filtersAreSelected,
		hasNextPage,
		incrementPageRequest,
		lastSuccessfulSearch,
		page,
		searchInputValue,
		selectedFilters,
		resultsPerPage,
		results,
		featuredResults,
		featuredTitle,
		loading,
		error,
		shouldSearch,
		totalMatches,
		searchIsEmpty,
	} = useSelector( state => ( {
		activeTab: getActiveTab( state ),
		description: getDescription( state ),
		filters: getFilters( state ),
		filtersAreSelected: hasSelectedFilters( state ),
		hasNextPage: getHasNextPage( state ),
		incrementPageRequest: getIncrementPageRequest( state ),
		lastSuccessfulSearch: getLastSuccessfulSearch( state ),
		page: getPage( state ),
		results: getResults( state ),
		featuredResults: getFeaturedResults( state ),
		featuredTitle: getFeaturedTitle( state ),
		resultsPerPage: getResultsPerPage( state ),
		searchInputValue: getSearchInputValue( state ),
		searchIsEmpty: isSearchEmpty( state ),
		loading: getLoading( state ),
		error: getError( state ),
		selectedFilters: getSelectedFilters( state ),
		shouldSearch: getShouldSearch( state ),
		totalMatches: getTotalMatches( state ),
	} ) );

	// Update URL when search input changes
	useEffect( () => {
		updateUrl( selectedFilters, searchInputValue );
	}, [searchInputValue] );

	// When Filters, change, set new active tab, update URL and initiate search if parameters differ
	useEffect( () => {
		dispatch( setActiveTab( '' ) );
		updateUrl( selectedFilters, searchInputValue );
		const requestSettingsState = {
			hasNextPage,
			incrementPageRequest,
			page,
			results,
			featuredResults,
			resultsPerPage,
			searchInputValue,
			selectedFilters,
		};
		const searchParametersDiffer = doSearchParametersDiffer( lastSuccessfulSearch, requestSettingsState );
		if ( searchParametersDiffer ) dispatch( setShouldSearch( true ) );
	}, [selectedFilters] );

	// Set filters when location or filters change
	useEffect( () => {
		dispatch( setSelectedFilters( getUrlFilters( location, filters ) ) );
	}, [location, filters] );

	// Change searchInputValue if location change provides new one in url
	useEffect( () => {
		const urlSearch = location.search || '';
		const urlSearchKeyword = queryString.parse( urlSearch ).keywords || '';
		if ( urlSearchKeyword !== searchInputValue ) dispatch( setSearchInputValue( urlSearchKeyword ) );
	}, [location] );

	// Initiate search
	useEffect( () => {
		if ( shouldSearch && !loading ) {
			// Collect state needed for fetching results
			const requestSettingsState = {
				hasNextPage,
				incrementPageRequest,
				page,
				results,
				featuredResults,
				featuredTitle,
				resultsPerPage,
				searchInputValue,
				selectedFilters,
				searchIsEmpty,
			};

			// console.log( 'requestSettingsState', requestSettingsState );

			// If different searches or filters, or time period has elapsed since clear results before fetching new results
			const searchParametersDiffer = doSearchParametersDiffer( lastSuccessfulSearch, requestSettingsState );
			if ( 0 < results.length && searchParametersDiffer )
				dispatch( clearResults() ); // TODO check if time period has elapsed since last successful search

			// Dispatch actions
			dispatch( setShouldSearch( false ) );
			dispatch( fetchResultsLoading() );

			// Fetch results from REST API (using IIFE to allow async)
			( async requestSettingsState => {
				const requestResponse = await runRequestREST( requestSettingsState, getFilterText );
				if ( !requestResponse.error ) {
					const builtState = buildStateREST(
						requestResponse,
						requestSettingsState
					);
					// console.log( 'builtState', builtState );
					dispatch( fetchResultsSuccess( builtState ) );
				} else {
					console.error( requestResponse ); // eslint-disable-line
					dispatch( fetchResultsError( { error: true } ) );
				}
			} )( requestSettingsState );
		} else {
			dispatch( setShouldSearch( false ) );
		}
	}, [shouldSearch, loading] );

	// Listen for page increment requests and tigger search
	useEffect( () => {
		if ( incrementPageRequest && !shouldSearch && !loading )
			dispatch( setShouldSearch( true ) );
	}, [incrementPageRequest] );

	// On page load, Fetch initial results based url query params or default results
	useEffect( () => {
		dispatch( setShouldSearch( true ) );
	}, [] );

	const submitButtonClasses = 'btn thick-border type--a7';

	// Results info text
	let resultsInfoText = null;
	const shownResults = results.length + featuredResults.length;
	if ( lastSuccessfulSearch.searchIsEmpty ) {
		resultsInfoText = null;
	} else if ( 0 < shownResults ) {
		resultsInfoText = `Showing ${shownResults === totalMatches && 1 < totalMatches ? 'all ' : ''}${shownResults} result${1 < shownResults ? 's' : ''}`;
		if ( totalMatches > shownResults ) resultsInfoText += ` out of ${totalMatches}`;
	} else if ( !loading && !error && !totalMatches ) {
		resultsInfoText = `No results for "${ lastSuccessfulSearch.searchInputValue ? lastSuccessfulSearch.searchInputValue : ' '}" Please check your spelling, remove filters, or enter a new search term.`;
	} else if ( error ) {
		resultsInfoText = 'There was an error fetching search results, please check your connectivity and try again.';
	}

	// Search Results Title
	let searchResultsTitle = 'Search Results';
	if ( featuredResults && 0 < featuredResults.length ) {
		searchResultsTitle = 'Featured Team Members';
	} else {
		searchResultsTitle = lastSuccessfulSearch.searchIsEmpty
			? null
			: `Search Results${ lastSuccessfulSearch.searchInputValue ? ` for "${  lastSuccessfulSearch.searchInputValue  }"` : ''}`;
	}
	
	// Featured Results Title
	const featuredResultsTitle = featuredTitle || 'Featured Results';

	return (
		<main id="main" className="main--no-hero">
			<MetaTags post_title="Search" description={description} />

			<div className="content-contain">
				<div className="content-contain__interior-lvl-one">
					<SearchForm
						className="form--search"
						onSubmit={event => {
							event.preventDefault();
							dispatch( setShouldSearch( true ) );
						}}
					>
						<div className="form-group--inline form-group--inline-break-mobile">
							<div className="form-group__label-group form-group__label-group--with-button">
								<input
									className="btn thick-border type--a7"
									type="search"
									name="search-field"
									id="search-field"
									placeholder={
										searchInputValue
											? searchInputValue
											: 'Search for people, locations, project types, services or content types…'
									}
									value={searchInputValue}
									onChange={event => {
										dispatch( setSearchInputValue( event.target.value ) );
									}}
								/>
								<button
									type="submit"
									className={submitButtonClasses}
								>
									Search
								</button>
							</div>

							<button
								onClick={event => {
									event.preventDefault();
									dispatch( resetSearch() );
								}}
								type="button"
								className="btn btn--clear thick-border btn--inverted type--a7 md-up"
							>
								Clear
							</button>
						</div>
					</SearchForm>

					<Tabbed
						filters={filters}
						selectedFilters={selectedFilters}
						toggleFilter={( groupIndex, filterName ) => {
							dispatch( toggleFilter( groupIndex, filterName ) );
						}}
						activeTab={activeTab}
						setActiveTab={e => {
							const activeTabName =
								e.currentTarget.getAttribute( 'data-tab' ) ||
								e.currentTarget.parentNode.id; // get id of active tab
							dispatch(
								setActiveTab(
									activeTabName !== activeTab ? activeTabName : ''
								)
							);
						}}
					/>

					<StyledResults>
						<StyledResultsInfo className="type--c5 type--rolling-stone">
							
							{filtersAreSelected && (
								<SearchResultsFor
									searchInputValue={searchInputValue}
									hasSelectedFilters={() => filtersAreSelected}
									selectedFilters={selectedFilters}
									removeFilter={( groupIndex, filterName ) => {
										dispatch( removeFilter( groupIndex, filterName ) );
									}}
									getFilterText={getFilterText}
								/>
							)}
							
							<StyledResultsInfoBlock>
								{0 < shownResults ? (
									<StyledResultsP>{resultsInfoText}</StyledResultsP>
								) : (
									<StyledNoResultsP className="type--c6 type--rolling-stone">{resultsInfoText}</StyledNoResultsP>
								)}
							</StyledResultsInfoBlock>
						</StyledResultsInfo>

						{0 < featuredResults.length && (
							<GridStandard
								content={featuredResults}
								cardStyle="search"
								title={featuredResultsTitle}
							/>
						)}

						{0 < results.length && (
							<GridStandard
								content={results}
								cardStyle="search"
								title={searchResultsTitle}
							/>
						)}

						<LoadingGrid loading={loading} numItems={resultsPerPage} />
					</StyledResults>

					<StyledLoadMoreContainer>
						<LoadMoreButton
							loadMoreFunction={() => { if ( hasNextPage ) dispatch( incrementPage( true ) ); }}
							loading={loading}
							hasMoreItems={hasNextPage}
							buttonVariation="solid"
							seeMoreText="Load More"
						/>
					</StyledLoadMoreContainer>
				</div>
			</div>
		</main>
	);
};

export default Search;

Search.propTypes = {
	location: PropTypes.string.isRequired,
	getFilterText: PropTypes.func.isRequired,
};

const LoadingGrid = ( { loading, numItems } ) => {
	if ( loading ) {
		return (
			<GridStandard
				cardStyle="search"
				loading={true}
				numLoadingItems={numItems}
				content={[]}
			/>
		);
	} else return null;
};

LoadingGrid.propTypes = {
	numItems: PropTypes.number,
	loading: PropTypes.bool.isRequired,
};

LoadingGrid.defaultProps = {
	numItems: 6,
};
